import { createAsyncThunk } from '@reduxjs/toolkit'
import { taskTrackerApi } from '../../api/taskTracker.api'
import { resolveTaskTrackerColumnSortApiParams } from '../../lib/taskTrackerColumnSort.utils'
import { selectTaskTrackerColumnSort } from '../columnSort/columnSort.selectors'
import type { TaskTrackerBoardColumn, TaskTrackerBoardStickerFilter } from '../domain.types'
import { getErrorMessage } from '../shared/thunk.utils'
import type { RootState } from '@store/redux.store'
import type { TaskTrackerState } from '../index'

type AppState = RootState & {
  taskTracker: TaskTrackerState
}

const getColumnSortApiParams = (
  state: AppState,
  boardId: number,
  columnId: number,
) => {
  const sort = selectTaskTrackerColumnSort(state, boardId, columnId)
  return resolveTaskTrackerColumnSortApiParams(sort.sortField, sort.sortOrder)
}

export const loadMoreColumnItemsThunk = createAsyncThunk(
  'taskTracker/loadMoreColumnItems',
  async (
    payload: {
      boardId: number
      columnId: number
      stickerFilters: TaskTrackerBoardStickerFilter[]
      isCompleted?: boolean
    },
    { getState, rejectWithValue },
  ) => {
    const state = getState() as AppState
    const kanban = state.taskTracker.boardKanban.byBoardId[payload.boardId]
    const column = kanban?.columns.find((item) => item.id === payload.columnId)
    if (!column) return rejectWithValue('Колонка не найдена')

    try {
      const cursor =
        column.cards.length > 0 ? String(column.cards.length) : undefined
      const page = await taskTrackerApi.getColumnItems({
        boardId: payload.boardId,
        columnId: payload.columnId,
        cursor,
        stickerFilters: payload.stickerFilters,
        isCompleted: payload.isCompleted,
        ...getColumnSortApiParams(state, payload.boardId, payload.columnId),
      })
      return {
        boardId: payload.boardId,
        columnId: payload.columnId,
        page,
      }
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось догрузить задачи колонки'),
      )
    }
  },
  {
    condition: (payload, { getState }) => {
      const state = getState() as AppState
      const { boardId, columnId } = payload
      const pagination =
        state.taskTracker.columnPagination[boardId]?.[columnId]

      if (pagination?.loadingMore) return false
      if (pagination && !pagination.hasMore) return false

      const laneColumn = state.taskTracker.boardKanban.byBoardId[
        boardId
      ]?.boardItems.find(
        (item) => item.type === 'column' && item.id === columnId,
      )

      if (
        laneColumn?.type === 'column' &&
        laneColumn.cardsHasMore === false
      ) {
        return false
      }

      return true
    },
  },
)

export const reloadColumnItemsThunk = createAsyncThunk(
  'taskTracker/reloadColumnItems',
  async (
    payload: {
      boardId: number
      columnId: number
      stickerFilters: TaskTrackerBoardStickerFilter[]
      isCompleted?: boolean
    },
    { getState, rejectWithValue },
  ) => {
    const state = getState() as AppState
    const kanban = state.taskTracker.boardKanban.byBoardId[payload.boardId]
    const column = kanban?.columns.find((item) => item.id === payload.columnId)
    if (!column) return rejectWithValue('Колонка не найдена')

    try {
      const page = await taskTrackerApi.getColumnItems({
        boardId: payload.boardId,
        columnId: payload.columnId,
        stickerFilters: payload.stickerFilters,
        isCompleted: payload.isCompleted,
        ...getColumnSortApiParams(state, payload.boardId, payload.columnId),
      })
      return {
        boardId: payload.boardId,
        columnId: payload.columnId,
        page,
      }
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось обновить задачи колонки'),
      )
    }
  },
)
